import React, { useEffect, useMemo, useState } from "react";
import toast from "react-hot-toast";
import {
    adminGetPromoStats,
    adminAssignPromoToCustomers,
    adminListPromoAssignedTemplates,
    adminListPromosLite,
} from "../../../Admin/adminApi";
import "./Promozioni.css";
import "./AdminPromozioniStats.css";
import useCityAutocomplete from "../../../hooks/useCityAutocomplete";
import Profilo from "../../Profilo";
import OrderDetailModal from "../../../components/orders/OrderDetailModal";

function eurFromCents(cents) {
    const n = Number(cents || 0) / 100;
    return n.toLocaleString("it-IT", { style: "currency", currency: "EUR" });
}

function fmtDate(iso) {
    if (!iso) return "—";
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return "—";
    return d.toLocaleDateString("it-IT", {
        day: "2-digit",
        month: "2-digit",
        year: "2-digit",
    });
}

function kindLabel(kind) {
    const k = String(kind || "").toLowerCase();
    if (k === "percent") return "Percentuale";
    if (k === "fixed") return "Importo fisso";
    if (k === "promoqta" || k === "qta") return "QTA";
    if (k === "basket" || k === "paniere") return "Paniere";
    if (k === "plain") return "Plain";
    return kind || "—";
}

export default function AdminPromozioniClientiStats() {
    const [loading, setLoading] = useState(false);

    const [filters, setFilters] = useState({
        days: 90,
        supplierId: "",
        promoId: "",
        city: "",
        province: "",
        q: "",
        page: 1,
        pageSize: 200,
    });

    const [promos, setPromos] = useState([]);
    const [serverFilters, setServerFilters] = useState(null);
    const [customersLast4, setCustomersLast4] = useState([]);

    const [promoOptions, setPromoOptions] = useState([]);
    const [promoOptionsLoading, setPromoOptionsLoading] = useState(false);

    const [selectedCustomers, setSelectedCustomers] = useState(() => new Set());
    const [assignPromoId, setAssignPromoId] = useState("");
    const [assignBusy, setAssignBusy] = useState(false);
    const [assignSendEmail, setAssignSendEmail] = useState(true);
    const [assignSendPush, setAssignSendPush] = useState(true);
    const [assignValidDays, setAssignValidDays] = useState(30);

    const [tplLoading, setTplLoading] = useState(false);
    const [templates, setTemplates] = useState([]);
    const [templateKey, setTemplateKey] = useState("default");

    const [openClientId, setOpenClientId] = useState(null);

    const [detailId, setDetailId] = useState(null);
    const [detailOpen, setDetailOpen] = useState(false);
    const [overview, setOverview] = useState(null);

    function openClientDetail(id) {
        if (!id) return;
        setOpenClientId(id);
    }

    function closeClientDetail() {
        setOpenClientId(null);
    }

    function openOrderDetail(id) {
        if (!id) return;
        setDetailId(id);
        setDetailOpen(true);
    }

    function closeOrderDetail() {
        setDetailOpen(false);
        setDetailId(null);
    }

    const cityAuto = useCityAutocomplete({
        citta: filters.city,
        provincia: filters.province,
        cap: "",
        enabled: true,
    });

    useEffect(() => {
        let alive = true;

        (async () => {
            try {
                setTplLoading(true);
                const res = await adminListPromoAssignedTemplates();
                if (!alive) return;

                const list = Array.isArray(res?.templates) ? res.templates : [];
                setTemplates(list);

                if (list.length && !list.some((t) => t.key === "default")) {
                    setTemplateKey(list[0].key);
                }
            } catch (e) {
                console.error(e);
                if (!alive) return;
                setTemplates([]);
                setTemplateKey("default");
            } finally {
                if (alive) setTplLoading(false);
            }
        })();

        return () => {
            alive = false;
        };
    }, []);

    useEffect(() => {
        let alive = true;

        (async () => {
            try {
                setPromoOptionsLoading(true);
                const res = await adminListPromosLite();
                if (!alive) return;
                setPromoOptions(Array.isArray(res?.promos) ? res.promos : []);
            } catch (e) {
                console.error(e);
                if (!alive) return;
                setPromoOptions([]);
            } finally {
                if (alive) setPromoOptionsLoading(false);
            }
        })();

        return () => {
            alive = false;
        };
    }, []);

    const loadStats = async () => {
        const supplierIdNum =
            String(filters.supplierId || "").trim() === ""
                ? null
                : Number(filters.supplierId);

        const noPromo = filters.promoId === "__none__";
        const promoIdNum =
            !noPromo && String(filters.promoId || "").trim() !== ""
                ? Number(filters.promoId)
                : null;

        const res = await adminGetPromoStats({
            days: Number(filters.days) || 90,
            expiringDays: Number(filters.expiringDays) || 7,
            supplierId: Number.isFinite(supplierIdNum) ? supplierIdNum : null,
            q: filters.q || "",
            promoId: Number.isFinite(promoIdNum) ? promoIdNum : null,
            noPromo,
            city: (filters.city || "").trim() || null,
            province: (filters.province || "").trim() || null,
            page: Number(filters.page) || 1,
            pageSize: Number(filters.pageSize) || 50,
        });

        setServerFilters(res?.filters || null);
        setOverview(res?.overview || null);
        setPromos(Array.isArray(res?.promos) ? res.promos : []);
        setCustomersLast4(
            Array.isArray(res?.customers_last4) ? res.customers_last4 : [],
        );
    };

    useEffect(() => {
        let alive = true;

        (async () => {
            try {
                setLoading(true);
                await loadStats();
            } catch (e) {
                console.error("[PromoClientiStats] load error:", e);
                toast.error(e?.message || "Errore caricamento statistiche promo");
                if (!alive) return;
                setOverview(null);
                setPromos([]);
                setServerFilters(null);
                setCustomersLast4([]);
            } finally {
                if (alive) setLoading(false);
            }
        })();

        return () => {
            alive = false;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        filters.promoId,
        filters.city,
        filters.province,
        filters.days,
        filters.expiringDays,
        filters.supplierId,
        filters.q,
        filters.page,
        filters.pageSize,
    ]);

    const viewPromos = useMemo(() => promos, [promos]);
    const viewLast4 = useMemo(() => customersLast4, [customersLast4]);

    const selectedCount = selectedCustomers.size;

    const toggleCustomer = (userId) => {
        const n = Number(userId);
        if (!Number.isFinite(n)) return;

        setSelectedCustomers((prev) => {
            const next = new Set(prev);
            if (next.has(n)) next.delete(n);
            else next.add(n);
            return next;
        });
    };

    const allCustomersSelectedOnView = useMemo(() => {
        if (!viewLast4.length) return false;

        for (const u of viewLast4) {
            const id = Number(u.user_id);
            if (!Number.isFinite(id)) continue;
            if (!selectedCustomers.has(id)) return false;
        }

        return true;
    }, [viewLast4, selectedCustomers]);

    const toggleAllCustomersOnView = () => {
        setSelectedCustomers((prev) => {
            const next = new Set(prev);
            const shouldSelect = !allCustomersSelectedOnView;

            for (const u of viewLast4) {
                const id = Number(u.user_id);
                if (!Number.isFinite(id)) continue;
                if (shouldSelect) next.add(id);
                else next.delete(id);
            }

            return next;
        });
    };

    const clearSelectedCustomers = () => setSelectedCustomers(new Set());

    const doAssignSelected = async () => {
        const pid = Number(assignPromoId);

        if (!Number.isFinite(pid) || pid <= 0) {
            toast.error("Seleziona una promo da assegnare.");
            return;
        }

        if (!selectedCount) {
            toast.error("Seleziona almeno un cliente.");
            return;
        }

        if (assignSendEmail && templates.length > 0) {
            const exists = templates.some((t) => t.key === templateKey);
            if (!exists) {
                toast.error("Template email non valido (non esiste).");
                return;
            }
        }

        const validDaysNum = Number(assignValidDays);
        const validDays =
            Number.isFinite(validDaysNum) && validDaysNum > 0 ? validDaysNum : null;

        const promoLabel = (() => {
            const p = viewPromos.find((x) => Number(x.id) === pid);
            if (!p) return `#${pid}`;
            const name = p.name || "—";
            const code = p.code ? ` (${p.code})` : "";
            return `#${p.id} · ${name}${code}`;
        })();

        let eligibility;
        try {
            const response=await fetch(`${process.env.REACT_APP_API_BASE_URL||''}/api/admin/promos/eligibility`,{
                method:'POST',headers:{'Content-Type':'application/json',Authorization:`Bearer ${localStorage.getItem('token')}`},
                body:JSON.stringify({customers:Array.from(selectedCustomers)})});
            if(!response.ok) throw new Error('unavailable');
            eligibility=await response.json();
        } catch {toast.error('Impossibile verificare i destinatari eleggibili. Riprova.');return;}
        const confirmText =
            `Stai per assegnare la promo:\n${promoLabel}\n\n` +
            `A clienti selezionati: ${selectedCount}\n` +
            `Eleggibili ora: email ${eligibility.email}, push ${eligibility.push}. Ricontrollati all’invio.\n` +
            `Email: ${assignSendEmail ? "SI" : "NO"}\n` +
            `Push: ${assignSendPush ? "SI" : "NO"}\n` +
            `Template email: ${assignSendEmail ? templateKey : "—"}\n` +
            `Validità: ${validDays ? `${validDays} giorni` : "default/nessuna"}\n\n` +
            `Procedo?`;

        if (!window.confirm(confirmText)) return;

        try {
            setAssignBusy(true);

            const payload = {
                customers: Array.from(selectedCustomers),
                sendEmail: !!assignSendEmail,
                sendPush: !!assignSendPush,
                valid_days: validDays,
                templateKey: assignSendEmail ? templateKey : null,
            };

            const token = localStorage.getItem("token");
            const res = await adminAssignPromoToCustomers(pid, payload, token);

            const ok = (res?.inserted?.length || 0) + (res?.updated?.length || 0);
            const fail = res?.failed?.length || 0;
            const mailFail = res?.email_failed?.length || 0;
            const pushFail = res?.push_failed?.length || 0;

            toast.success(
                `Assegnata a ${ok} clienti` +
                (fail ? ` · ${fail} errori DB` : "") +
                (mailFail ? ` · ${mailFail} email KO` : "") +
                (pushFail ? ` · ${pushFail} push KO` : ""),
                // Assignment does not imply marketing delivery.
            );
            toast(`${res?.email_suppressed||0} email e ${res?.push_suppressed||0} push non inviate per preferenze o disponibilità`);

            clearSelectedCustomers();
            setLoading(true);
            await loadStats();
            setLoading(false);
        } catch (e) {
            console.error(e);
            toast.error(e?.message || "Errore assegnazione promo");
        } finally {
            setAssignBusy(false);
        }
    };

    const renderPromoCell = (pp) => {
        if (!pp) return <span className="muted">—</span>;

        const used = !!pp.used;

        const activatedAt =
            pp.effective_from ||
            pp.assigned_at ||
            pp.valid_from ||
            pp.starts_at ||
            null;

        const expAt = pp.effective_until || pp.valid_until || pp.ends_at || null;

        return (
            <div className="last4-line" title={pp.promo_name || ""}>
                <span className="last4-line-name">{pp.promo_name || "—"}</span>
                <span className="last4-line-meta">{fmtDate(activatedAt)}</span>
                <span className="last4-line-meta">{fmtDate(expAt)}</span>
                {used ? (
                    pp.order_id ? (
                        <button
                            type="button"
                            className="last4-line-flag-btn"
                            onClick={() => openOrderDetail(pp.order_id)}
                            title={`Apri ordine #${pp.order_id}`}
                            aria-label={`Apri ordine #${pp.order_id}`}
                        >
                            <span
                                className="last4-line-flag is-yes is-clickable"
                                aria-hidden="true"
                            >
                                ✓
                            </span>
                        </button>
                    ) : (
                        <span
                            className="last4-line-flag is-yes"
                            title="Promo usata"
                            aria-label="Promo usata"
                        >
                            ✓
                        </span>
                    )
                ) : (
                    <span
                        className="last4-line-flag is-no"
                        title="Promo non usata"
                        aria-label="Promo non usata"
                    >
                        ✗
                    </span>
                )}
            </div>
        );
    };

    return (
        <div className="promo-page aps-page">
            <div className="promo-shell">
                <div className="promo-shell-inner">
                    <div className="promo-page-section">
                        <div className="promo-toolbar promo-toolbar--page">
                            <div className="promo-toolbar-left">
                                <label className="promo-check">
                                    <span>Promo</span>
                                    <select
                                        className="promo-create-select"
                                        value={filters.promoId}
                                        onChange={(e) =>
                                            setFilters((p) => ({
                                                ...p,
                                                promoId: e.target.value,
                                                page: 1,
                                            }))
                                        }
                                        title="Filtra per promo"
                                        disabled={promoOptionsLoading}
                                    >
                                        <option value="">Tutte</option>
                                        <option value="__none__">Nessuna promo</option>
                                        {promoOptions.map((p) => (
                                            <option key={p.id} value={p.id}>
                                                #{p.id} · {p.name || "—"} {p.code ? `· ${p.code}` : ""}
                                            </option>
                                        ))}
                                    </select>
                                </label>

                                <label className="promo-check">
                                    <span>Finestra</span>
                                    <select
                                        className="promo-create-select"
                                        value={filters.days}
                                        onChange={(e) =>
                                            setFilters((p) => ({
                                                ...p,
                                                days: Number(e.target.value),
                                                page: 1,
                                            }))
                                        }
                                    >
                                        <option value={7}>7 giorni</option>
                                        <option value={30}>30 giorni</option>
                                        <option value={90}>90 giorni</option>
                                        <option value={365}>365 giorni</option>
                                    </select>
                                </label>
                            </div>

                            <div className="aps-city-wrap">
                                <label className="promo-check aps-city">
                                    <span>Città</span>
                                    <input
                                        className="promo-search"
                                        placeholder="Es. Milano"
                                        value={filters.city}
                                        onChange={(e) => {
                                            const nextCity = e.target.value;
                                            setFilters((p) => ({
                                                ...p,
                                                city: nextCity,
                                                province: p.province ? "" : p.province,
                                                page: 1,
                                            }));
                                            cityAuto.setSuggestions([]);
                                        }}
                                    />
                                </label>

                                {cityAuto.suggestions?.length > 0 &&
                                    (filters.city || "").trim().length >= 2 && (
                                        <div className="aps-city-dd">
                                            {cityAuto.suggestions.slice(0, 8).map((s, idx) => {
                                                const c =
                                                    s?.name || s?.citta || s?.city || s?.nome || "";
                                                const pr =
                                                    s?.provincia || s?.province || s?.sigla || "";
                                                const label = pr ? `${c} (${pr})` : c;

                                                return (
                                                    <button
                                                        key={`${c}-${pr}-${idx}`}
                                                        type="button"
                                                        className="aps-city-dd-item"
                                                        onClick={() => {
                                                            setFilters((p) => ({
                                                                ...p,
                                                                city: c,
                                                                province: pr || p.province,
                                                                page: 1,
                                                            }));
                                                            cityAuto.setSuggestions([]);
                                                        }}
                                                        title={label}
                                                    >
                                                        {label}
                                                    </button>
                                                );
                                            })}
                                        </div>
                                    )}
                            </div>

                            <label className="promo-check">
                                <span>Provincia</span>
                                <input
                                    className="promo-search"
                                    style={{ maxWidth: 110 }}
                                    placeholder="Es. MI"
                                    value={filters.province}
                                    onChange={(e) =>
                                        setFilters((p) => ({
                                            ...p,
                                            province: e.target.value,
                                            page: 1,
                                        }))
                                    }
                                />
                            </label>

                            <div className="promo-toolbar-right">
                                <span className="muted">
                                    {serverFilters
                                        ? `days=${serverFilters.days}`
                                        : loading
                                            ? "carico…"
                                            : ""}
                                </span>
                            </div>
                        </div>

                        <div className="group-card">
                            <div className="group-head">
                                <div className="gh-left plain">
                                    <div className="gh-title">Clienti · ultime 4 promo</div>
                                    <div className="gh-count">{viewLast4.length}</div>
                                </div>
                            </div>

                            <div className="group-body">
                                {loading && <div className="promo-empty">Caricamento…</div>}

                                {!loading && viewLast4.length === 0 && (
                                    <div className="promo-empty">
                                        Nessun cliente con promo assegnate (last4 vuoto).
                                    </div>
                                )}

                                {!loading && viewLast4.length > 0 && (
                                    <div className="table-wrap aps-last4-wrap">
                                        <div className="table table-stats-last4">
                                            <div className="thead">
                                                <div className="col-check">
                                                    <input
                                                        type="checkbox"
                                                        checked={allCustomersSelectedOnView}
                                                        onChange={toggleAllCustomersOnView}
                                                        title="Seleziona tutti (lista corrente)"
                                                    />
                                                </div>
                                                <div>Cliente</div>
                                                <div className="ta-center">Promo 1</div>
                                                <div className="ta-center">Promo 2</div>
                                                <div className="ta-center">Promo 3</div>
                                                <div className="ta-center">Promo 4</div>
                                            </div>

                                            {viewLast4.map((u) => {
                                                const uid = Number(u.user_id);
                                                const checked =
                                                    Number.isFinite(uid) && selectedCustomers.has(uid);

                                                const promosArr = Array.isArray(u.promos) ? u.promos : [];
                                                const p = (i) => promosArr[i] || null;

                                                return (
                                                    <div className="trow" key={u.user_id}>
                                                        <div className="col-check">
                                                            <input
                                                                type="checkbox"
                                                                checked={checked}
                                                                onChange={() => toggleCustomer(uid)}
                                                                title="Seleziona cliente"
                                                            />
                                                        </div>

                                                        <div className="cell-name">
                                                            <div className="name-wrap">
                                                                {u.user_id ? (
                                                                    <button
                                                                        type="button"
                                                                        className="aps-link-btn"
                                                                        onClick={() => openClientDetail(u.user_id)}
                                                                        title={`Apri cliente #${u.user_id}`}
                                                                    >
                                                                        <span className="name-text">
                                                                            {u.full_name || `Utente #${u.user_id}`}
                                                                        </span>
                                                                    </button>
                                                                ) : (
                                                                    <span className="name-text">
                                                                        {u.full_name || `Utente #${u.user_id}`}
                                                                    </span>
                                                                )}
                                                            </div>
                                                        </div>

                                                        <div className="ta-center">{renderPromoCell(p(0))}</div>
                                                        <div className="ta-center">{renderPromoCell(p(1))}</div>
                                                        <div className="ta-center">{renderPromoCell(p(2))}</div>
                                                        <div className="ta-center">{renderPromoCell(p(3))}</div>
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    </div>
                                )}

                                <div className="promo-toolbar promo-toolbar--page aps-assignbar">
                                    <div className="promo-toolbar-left aps-assign-left">
                                        <div className="aps-selected">
                                            <span className="muted">Selezionati:</span>
                                            <b>{selectedCount}</b>
                                        </div>

                                        <label className="promo-check aps-validity">
                                            <span>Validità</span>
                                            <input
                                                type="number"
                                                min="1"
                                                step="1"
                                                value={assignValidDays}
                                                onChange={(e) => setAssignValidDays(e.target.value)}
                                                className="promo-search aps-validdays"
                                            />
                                            <span className="aps-validity-unit">giorni</span>
                                        </label>

                                        <div className="assign-channels">
                                            <label className="promo-check">
                                                <input
                                                    type="checkbox"
                                                    checked={assignSendEmail}
                                                    onChange={(e) => setAssignSendEmail(e.target.checked)}
                                                />
                                                <span>Email</span>
                                            </label>

                                            <label className="promo-check">
                                                <input
                                                    type="checkbox"
                                                    checked={assignSendPush}
                                                    onChange={(e) => setAssignSendPush(e.target.checked)}
                                                />
                                                <span>Push</span>
                                            </label>

                                            <select
                                                className="promo-create-select assign-template-select"
                                                value={templateKey}
                                                onChange={(e) => setTemplateKey(e.target.value)}
                                                disabled={
                                                    !assignSendEmail ||
                                                    tplLoading ||
                                                    templates.length === 0
                                                }
                                                title="Template email"
                                            >
                                                {templates.length === 0 ? (
                                                    <option value="default">default</option>
                                                ) : (
                                                    templates.map((t) => (
                                                        <option key={t.key} value={t.key}>
                                                            {t.label}
                                                            {t.hasImages ? " (img)" : ""}
                                                        </option>
                                                    ))
                                                )}
                                            </select>

                                            <select
                                                className="promo-create-select assign-promo-select"
                                                value={assignPromoId}
                                                onChange={(e) => setAssignPromoId(e.target.value)}
                                                title="Promo da assegnare"
                                            >
                                                <option value="">Seleziona promo…</option>
                                                {viewPromos.map((p) => (
                                                    <option key={p.id} value={p.id}>
                                                        #{p.id} · {p.name || "—"}{" "}
                                                        {p.code ? `· ${p.code}` : ""} · {kindLabel(p.kind)}
                                                    </option>
                                                ))}
                                            </select>
                                        </div>
                                    </div>

                                    <div className="promo-toolbar-right aps-assign-right">
                                        <button
                                            type="button"
                                            className="promo-btn promo-btn--primary"
                                            disabled={
                                                assignBusy ||
                                                loading ||
                                                selectedCount === 0 ||
                                                !assignPromoId
                                            }
                                            onClick={doAssignSelected}
                                            title="Assegna promo ai clienti selezionati"
                                        >
                                            {assignBusy ? "Assegno…" : "Assegna promo"}
                                        </button>

                                        <button
                                            type="button"
                                            className="promo-btn promo-btn--ghost"
                                            disabled={assignBusy || selectedCount === 0}
                                            onClick={clearSelectedCustomers}
                                            title="Svuota selezione"
                                        >
                                            Svuota
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </div>
                        {/* fine clienti */}
                    </div>
                </div>
            </div>
            {openClientId && (
                <div className="ac-overlay" role="dialog" aria-modal="true">
                    <div className="ac-modal">
                        <div className="ac-modal-h">
                            <div className="ac-title">Cliente #{openClientId}</div>
                            <button className="ac-close" onClick={closeClientDetail}>
                                ✕
                            </button>
                        </div>

                        <div className="ac-modal-body">
                            <div className="ac-form">
                                <div className="ac-profile-embed">
                                    <Profilo embedded targetUserId={openClientId} />
                                </div>
                            </div>
                        </div>

                        <div className="ac-modal-f">
                            <button className="ac-btn" onClick={closeClientDetail}>
                                Chiudi
                            </button>
                        </div>
                    </div>
                </div>
            )}
            <OrderDetailModal
                open={detailOpen}
                orderId={detailId}
                onClose={closeOrderDetail}
            />
        </div>
    );
}
